import {AxiosResponse, AxiosError, AxiosRequestConfig} from 'axios'
import * as humps from 'humps'
import {omit} from 'lodash-es'
import {RestypedBase} from 'restyped/spec'
import axios, {ITypedAxiosStrictInstance} from 'common/types/api/restyped'
import {Api} from 'common/types/api'
import {ApiError} from 'common/lib/errors'
import * as qs from 'qs'
import config from 'common/config'

type FactoryOptions = AxiosRequestConfig & {
  headers?: {
    [k: string]: string;
  };
  baseURL?: string;
    /**
     * При запросе преобразуем ключи в lo_dash-нотацию (данные запроса и
     * query-параметры), ответ преобразуем в camelCase нотацию
     */
  humpKeys?: boolean;
  setCookie? (setCookieStr: string | string[]): void;
}

export type IApi<T extends RestypedBase> = ITypedAxiosStrictInstance<T>

function createResponseSuccessInterceptor (params: FactoryOptions) {
  return function responseInterceptor (res: AxiosResponse): AxiosResponse {
    const {method} = res.request

    if (
            (res.status === 204 && (method === 'post' || method === 'put')) ||
            method === 'delete'
        ) {
      res.data = {status: true}
    } else if (params.humpKeys) {
      res.data = humps.camelizeKeys(res.data)
    }

    return res
  }
}

function createResponseErrorInterceptor (params: FactoryOptions) {
  return function errorInterceptor (
    err: AxiosError
    ): AxiosResponse | Promise<void> {
    const res = err.response!

    if (res && res.headers && params.setCookie) {
      for (const k in res.headers) {
        if (
                    res.headers.hasOwnProperty(k) &&
                    k.toLowerCase() === 'set-cookie'
                ) {
          params.setCookie(res.headers[k])
          break
        }
      }
    }

    const data = res && res.data

    if (
            params.humpKeys &&
            data &&
            (data.err || data.error || data.errors)
        ) {
      res.data = humps.camelizeKeys(res.data)
    }

    const {config} = err
    const message = [
            err.toString(),
            `method: ${config.method && config.method.toUpperCase()}`,
            `url: ${config.url}?${config.paramsSerializer!(config.params)}`,
            config.data && `data: ${JSON.stringify(config.data)}`
        ]
            .filter(Boolean)
            .join('; ')
    const error = new ApiError(res, message)

    return Promise.reject(error)
  }
}

function createRequestInterceptor (params: FactoryOptions) {
  return function requestInterceptor (config: AxiosRequestConfig) {
    if (params.humpKeys && config.data && typeof config.data === 'object') {
      config.data = humps.decamelizeKeys(config.data)
    }

    return config
  }
}

/**
 * Создаем instance api
 */
export function createApiInstance<T extends RestypedBase> (
  params: FactoryOptions = {}
): ITypedAxiosStrictInstance<T> {
  const {humpKeys, ...otherParams} = params

  const api = axios.create<T>({
    withCredentials: true,
    paramsSerializer (queryParams) {
      return qs.stringify(
        humpKeys ? humps.decamelizeKeys(queryParams) : queryParams
            )
    },
    ...omit(otherParams, 'setCookie')
  })

  api.interceptors.request.use(createRequestInterceptor(params))
  api.interceptors.response.use(
    createResponseSuccessInterceptor(params),
    createResponseErrorInterceptor(params)
    )

  return api
}

export function createApi (): Api {
  return {
    ctt: createApiInstance({
      baseURL: config.api.cttBaseURL + config.api.cttPrefix,
      withCredentials: true
    }),
    centerRobot: createApiInstance({
      baseURL: config.api.centerBaseURL + config.api.centerPrefix,
      humpKeys: true,
      withCredentials: true
    }),
    supportMisc: createApiInstance({
      baseURL:
                config.api.supportMiscBaseURL + config.api.supportMiscPrefix,
      withCredentials: true
    })
  }
}
